import { request, requestNoContent } from './client';

/**
 * Tình trạng theo dõi một liên hệ khách gửi từ form trên website. Giá trị số khớp với máy chủ.
 */
export const ContactFollowUpStatus = {
  /** Chưa ai liên hệ. */
  New: 1,
  /** Đã gọi hoặc nhắn nhưng chưa xong. */
  InProgress: 2,
  /** Đã liên hệ xong. */
  Contacted: 3,
  /** Gọi, nhắn không được — vẫn phải làm tiếp. */
  Unreachable: 4,
  /** Thư rác, gửi nhầm, không phải nhu cầu thật. */
  Irrelevant: 5,
} as const;

/** Giá trị hợp lệ của tình trạng theo dõi. */
export type ContactFollowUpStatusValue =
  (typeof ContactFollowUpStatus)[keyof typeof ContactFollowUpStatus];

/**
 * Kênh khách liên hệ khi nhân viên nhập tay một liên hệ. Giá trị số khớp với máy chủ; máy chủ ghi
 * kênh thành nhãn ở đầu lời nhắn, ví dụ "[Zalo] Cần báo giá".
 */
export const ContactChannel = {
  Zalo: 1,
  Phone: 2,
  Email: 3,
  InPerson: 4,
  Other: 5,
} as const;

/** Giá trị hợp lệ của kênh liên hệ. */
export type ContactChannelValue = (typeof ContactChannel)[keyof typeof ContactChannel];

/** Một liên hệ từ website kèm kết quả theo dõi bên ERP. */
export interface ContactRequestItem {
  /** Định danh bên website. */
  readonly id: number;
  /** Họ và tên ghép theo thứ tự tiếng Việt. */
  readonly fullName: string;
  /** Tên (ô "Tên" trên form website). */
  readonly firstName: string | null;
  /** Họ (ô "Họ" trên form website). */
  readonly lastName: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly service: string | null;
  readonly message: string | null;
  /** Lúc khách gửi (hoặc lúc nhân viên nhập tay), dạng ISO 8601. */
  readonly submittedAt: string | null;
  /** Nhân viên nhập tay trên ERP (khách nhắn Zalo, gọi điện…), không phải khách gửi qua form. */
  readonly isManual: boolean;
  readonly status: ContactFollowUpStatusValue;
  readonly note: string | null;
  readonly handledByUserId: string | null;
  readonly handledByName: string | null;
  readonly handledAt: string | null;
}

/** Số liên hệ theo từng tình trạng, tính trên toàn bộ. */
export interface ContactRequestCounts {
  readonly needsAction: number;
  readonly new: number;
  readonly inProgress: number;
  readonly contacted: number;
  readonly unreachable: number;
  readonly irrelevant: number;
  readonly total: number;
}

/** Một trang liên hệ kèm số đếm theo tình trạng. */
export interface ContactRequestPage {
  readonly items: readonly ContactRequestItem[];
  readonly totalCount: number;
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly totalPageCount: number;
  readonly counts: ContactRequestCounts;
}

/** Điều kiện lọc danh sách liên hệ. */
export interface ContactRequestFilter {
  readonly status?: ContactFollowUpStatusValue;
  /** Chỉ lấy liên hệ còn phải làm: chưa liên hệ, đang liên hệ, chưa liên lạc được. */
  readonly needsAction?: boolean;
  readonly searchTerm?: string;
  readonly pageNumber?: number;
  readonly pageSize?: number;
}

/** Kết quả theo dõi cần ghi. */
export interface RecordContactFollowUpPayload {
  readonly status: ContactFollowUpStatusValue;
  readonly note: string | null;
}

/** Thông tin mới của một liên hệ, ghi thẳng sang website. Ô bỏ trống gửi `null`. */
export interface UpdateContactRequestPayload {
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  /** Mã dịch vụ, ví dụ `dao-tao`. */
  readonly service: string | null;
  readonly message: string | null;
}

/**
 * Liên hệ nhân viên nhập tay, thêm thẳng sang website. Ô bỏ trống gửi `null`; `message` là lời nhắn
 * chưa kèm nhãn kênh — máy chủ tự ghép.
 */
export interface CreateContactRequestPayload extends UpdateContactRequestPayload {
  readonly channel: ContactChannelValue;
}

const baseRoute = '/api/v1/sales/contact-requests';

/** Dựng phần truy vấn của địa chỉ, bỏ hẳn tham số rỗng. */
function buildQueryString(filter: ContactRequestFilter): string {
  const parameters = new URLSearchParams();

  if (filter.status !== undefined) {
    parameters.set('status', String(filter.status));
  }

  if (filter.needsAction === true) {
    parameters.set('needsAction', 'true');
  }

  if (filter.searchTerm !== undefined && filter.searchTerm.trim() !== '') {
    parameters.set('searchTerm', filter.searchTerm.trim());
  }

  if (filter.pageNumber !== undefined) {
    parameters.set('pageNumber', String(filter.pageNumber));
  }

  if (filter.pageSize !== undefined) {
    parameters.set('pageSize', String(filter.pageSize));
  }

  const query = parameters.toString();

  return query === '' ? '' : `?${query}`;
}

/** Lấy một trang liên hệ khách gửi từ website. */
export async function listContactRequests(
  filter: ContactRequestFilter,
): Promise<ContactRequestPage> {
  return await request<ContactRequestPage>(`${baseRoute}${buildQueryString(filter)}`);
}

/** Ghi kết quả liên hệ lại với khách. */
export async function recordContactFollowUp(
  id: number,
  payload: RecordContactFollowUpPayload,
): Promise<ContactRequestItem> {
  return await request<ContactRequestItem>(`${baseRoute}/${String(id)}/follow-up`, {
    method: 'PUT',
    body: payload,
  });
}

/**
 * Thêm liên hệ nhân viên nhập tay — khách nhắn Zalo, gọi điện… — ghi thẳng sang website. Trả về
 * liên hệ vừa thêm, tình trạng "Chưa liên hệ".
 */
export async function createContactRequest(
  payload: CreateContactRequestPayload,
): Promise<ContactRequestItem> {
  return await request<ContactRequestItem>(baseRoute, {
    method: 'POST',
    body: payload,
  });
}

/** Sửa họ, tên, cách liên lạc, dịch vụ và lời nhắn của một liên hệ — ghi thẳng sang website. */
export async function updateContactRequest(
  id: number,
  payload: UpdateContactRequestPayload,
): Promise<ContactRequestItem> {
  return await request<ContactRequestItem>(`${baseRoute}/${String(id)}`, {
    method: 'PUT',
    body: payload,
  });
}

/**
 * Xóa mềm một liên hệ trên website: website đánh dấu đã xóa, dòng vẫn còn nhưng ERP không hiện và
 * không khôi phục được.
 */
export async function deleteContactRequest(id: number): Promise<void> {
  await requestNoContent(`${baseRoute}/${String(id)}`, { method: 'DELETE' });
}
